import { useState } from "react"
import { AppRefresh } from "event-definitions"

// The main app (`index-main.js`) performs a deliberate one-time, app-wide remount shortly
// after boot: 50ms after the lazy bundle (`_lazy.js`) loads it calls `AppRefresh.raise()`,
// which bumps the `key` on the `<Fragment key={id}>` wrapping the router in `App.js`,
// tearing down and recreating the entire route subtree (see App.js).
//
// On the unauthenticated path the passwordless-login form mounts as soon as auth initialises,
// which is BEFORE that one-time remount. The remount then unmounts and recreates the login
// subtree (the email input gets a fresh React instance / nanoid id / empty value). E2E logins
// that start typing into the email field in that window hit a detaching element and fail with
// "cy.type() failed because it targeted a disabled element".
//
// Previously the unauthenticated branch in AuthGuard was additionally gated behind the
// `useSelectedClient()` `clientsLoading || loading` wait, which incidentally delayed the login
// form's first mount until after the remount had settled, hiding the race. That wait was
// removed when the guard was refactored. This hook restores a *minimal, targeted* readiness
// gate that waits only for the one thing that actually flips — the `AppRefresh` remount — so
// the login form mounts exactly once.
//
// `index-main.js` sets `window.__appWillRefresh` synchronously at import to signal that this
// entry point schedules the boot-time `AppRefresh`. Apps that do not (e.g. the Capacitor
// companion app, whose `index-app.js` never raises `AppRefresh` at boot) leave the flag unset,
// so the gate is open immediately there and login is never delayed.

let appRefreshFired = false

AppRefresh.handleOnce(() => {
    appRefreshFired = true
})

export function isLoginMountReady() {
    if (appRefreshFired) return true
    // Only the main app schedules the boot-time AppRefresh remount. If this entry point does
    // not, there is no remount to wait for and the login form is safe to mount immediately.
    return typeof window !== "undefined" && !window.__appWillRefresh
}

export function useLoginMountReady() {
    const [, refresh] = useState(0)
    AppRefresh.useEvent(() => refresh((n) => n + 1))
    return isLoginMountReady()
}
